'use client';
import { useState } from 'react';

import Popover from '@/components/atoms/Popover';
import { Text } from '@/components/atoms/Text';

import { SvgIcon } from '@/components/SvgIcon';
import { IconButton } from '@/components/atoms/IconButton';
import { useTranslation } from '@/lib/i18n/i18n.client';
import { cn } from '@/lib/utils';
import iconShareSrc from '~/svg/IconShare.svg';
import ShareCopyLink from './ShareCopyLink';
import { PostShareMenuProps } from './types';
import usePostShare from './usePostShare';

const PostShareMenu = ({
  url,
  title,
  shareOptionsIds,
  noText,
}: PostShareMenuProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { texts } = useTranslation();
  const { displayedSocialShareOptions, shouldDisplayCopyButton } = usePostShare(
    { url, title: title ?? '', shareOptionsIds: shareOptionsIds ?? [] }
  );
  return (
    <div
      data-tracker-component='share-menu'
      className={cn('md:w-auto', !noText && 'w-full')}
    >
      <Popover
        target={
          <div
            className={cn(
              'flex items-center justify-center gap-2 border-2 border-brand-500 rounded px-6 py-1.5',
              noText && '!px-2 !py-2'
            )}
          >
            {!noText && (
              <Text
                variant='button-text-m'
                classNames={{ wrapper: 'text-brand-500 font-serif' }}
              >
                {texts.blog.share}
              </Text>
            )}
            <SvgIcon src={iconShareSrc} className='h-5 w-5 text-brand-500' />
          </div>
        }
        showArrow={false}
        targetProps={{ asChild: true }}
        contentProps={{
          className: 'mb-3 w-[232px]',
          sideOffset: 12,
        }}
        isOpen={isOpen}
        position='top'
        onChange={setIsOpen}
        classNames={{
          childrenWrapper: 'rounded border-[1px] border-gray-300 shadow-md p-0',
        }}
      >
        <div
          className='relative flex flex-col'
          data-tracker-component='social-share'
        >
          <div className='flex items-center justify-between px-1 pt-3'>
            <Text
              variant='body-l'
              classNames={{
                wrapper: 'text-body-l text-gray-700 pl-2',
              }}
            >
              {texts.blog.share_in}
            </Text>
          </div>
          <div className='flex items-center justify-between mx-3 my-4'>
            {displayedSocialShareOptions.map(({ id, icon, label, url }) => (
              <IconButton
                key={id}
                variant='text'
                href={url}
                as='a'
                color='secondary'
                size='m'
                classNames={{ inner: 'h-5 w-5' }}
              >
                <SvgIcon src={icon} className='text-base-black h-5 w-5' />
                <span className='sr-only'>{label}</span>
              </IconButton>
            ))}
            {shouldDisplayCopyButton && <ShareCopyLink url={url} />}
          </div>
        </div>
      </Popover>
    </div>
  );
};

export default PostShareMenu;
